import React, { useEffect, useRef, useState } from 'react';
import './About.css';

function About() {
  const containerRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // add a small delay before showing the card
            setTimeout(() => setVisible(true), 300);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="about-section" role="region" aria-labelledby="about-heading">
      <div className="about-container" ref={containerRef}>
        <h2 id="about-heading" className="about-heading">About Us</h2>
        <div className={`about-text ${visible ? 'visible' : ''}`}>
          <h3 className="about-subheading">A Next-Generation Antibody–Drug Conjugate Company</h3>
          <p>
            Styx Biotechnologies is a precision oncology company advancing next-generation Antibody–Drug Conjugates (ADCs) for
            cancers with high unmet medical need. Founded by leaders with deep expertise across immuno-oncology, antibody engineering,
            and translational drug development, Styx combines scientific rigor with an execution-focused operating model to efficiently
            translate discovery into clinical candidates.
          </p>

          <p>
            We are building a differentiated ADC portfolio designed to address tumor heterogeneity, resistance, and delivery challenges.
          </p>

          <h3 className="about-subheading">Engineering the Next Era of ADC Therapeutics</h3>
          <p>At Styx, we believe the future of ADCs lies in the thoughtful integration of three critical elements:</p>
          <ul>
            <li>Tumor-selective targeting</li>
            <li>Optimized linker–payload strategy</li>
            <li>Translationally driven design</li>
          </ul>

          <p>
            Our approach leverages clinically validated payload classes integrated into optimized ADC architectures to enhance tumor
            penetration, improve therapeutic index, and support durable anti-tumor responses. By combining strong target biology with
            rational ADC design, we aim to broaden the therapeutic window and unlock the full potential of precision oncology.
          </p>

          <h3 className="about-subheading">Focused on High Unmet Need Cancers</h3>
          <p>
            We concentrate on cancers where current therapies fail to deliver durable and meaningful benefit. Despite advances in
            oncology, many aggressive solid tumors remain resistant to existing treatments, leaving patients with limited options.
            For patients and families confronting difficult diagnoses, we are committed to advancing therapies designed to offer
            new hope and meaningful progress.
          </p>

          <h3 className="about-subheading">Platform Strength and Intellectual Property</h3>
          <p>
            Styx maintains strategic access to a broad and validated panel of monoclonal antibodies, enabling rapid lead candidate
            selection and accelerated program initiation across diverse oncology targets. The company has established a strong
            intellectual property position, securing exclusive worldwide therapeutic licensing rights for selected antibodies to
            support long-term value creation and portfolio expansion.
          </p>

          <h3 className="about-subheading">Translating Innovation Into Meaningful Clinical Impact</h3>
          <p>
            At Styx, innovation goes beyond molecular design — it is driven by clinical relevance and real patient outcomes. By
            engineering ADCs designed to overcome resistance and improve precision and tolerability, we aim to deliver therapies
            that make a meaningful difference for patients and communities.
          </p>
        </div>
      </div>
    </section>
  );
}

export default About;

